<template>
    <div>
        <b-card no-body>
            <b-card-header header-bg-variant="primary">
                <span class="lead">{{ $t('menu.tasks') }}</span>&nbsp;
                <span class="badge badge-info">{{ taskCount }}</span>
                <b-button variant="info" class="float-right pb-0" v-b-tooltip.hover.d250="$t('files.context.createTask')" @click="createTask()" :disabled="!taskListId || !projectPermissions.includes('CREATE_TASK')"><icon name="plus"></icon></b-button>
                <b-form-radio-group
                    class="float-right mr-5"
                    buttons
                    size="sm"
                    v-model="filter.selected"
                    :options="filter.options">
                </b-form-radio-group>
            </b-card-header>
            <b-card-body>
                <div v-show="tasksToDisplay.length === 0">{{ $t('tasks.noTasks') }}</div>
                <table class="table table-hover" v-show="tasksToDisplay.length > 0" >
                    <thead>
                        <tr>
                            <th class="global-cursor-pointer" @click="handleSort('complete')" style="width:10%">
                                {{ $t('tasks.completed') }}
                                <icon name="caret-up" v-if="sorting.column === 'complete' && sorting.direction === 'asc'"></icon>
                                <icon name="caret-down" v-if="sorting.column === 'complete' && sorting.direction === 'desc'"></icon>
                            </th>
                            <th class="global-cursor-pointer" @click="handleSort('subject')" style="width:30%">
                                {{ $t('tasks.task') }}
                                <icon name="caret-up" v-if="sorting.column === 'subject' && sorting.direction === 'asc'"></icon>
                                <icon name="caret-down" v-if="sorting.column === 'subject' && sorting.direction === 'desc'"></icon>
                            </th>
                            <th class="global-cursor-pointer" @click="handleSort('due')" style="width:15%">
                                {{ $t('tasks.due') }}
                                <icon name="caret-up" v-if="sorting.column === 'due' && sorting.direction === 'asc'"></icon>
                                <icon name="caret-down" v-if="sorting.column === 'due' && sorting.direction === 'desc'"></icon>
                            </th>
                            <th class="global-cursor-pointer" @click="handleSort('modified')" style="width:15%">
                                {{ $t('tasks.modified') }}
                                <icon name="caret-up" v-if="sorting.column === 'modified' && sorting.direction === 'asc'"></icon>
                                <icon name="caret-down" v-if="sorting.column === 'modified' && sorting.direction === 'desc'"></icon>
                            </th>
                            <th style="width:15%">{{ $t('tasks.assignedTo') }}</th>
                            <th style="width:15%"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <task v-for="t in tasksToDisplay" :key="t.id" :task="t" :task-list-id="taskListId"></task>
                    </tbody>
                </table>
            </b-card-body>
        </b-card>

        <task-dialog :task-list-id="taskListId"></task-dialog>

        <typeahead-dialog
            dialog-id="dlgTypeahead_ProjectTasks"
            :dialog-title="$t('tasks.assignProjectMember')"
            :field-label="$t('tasks.projectMembers') + ':'"
            :field-placeholder="$t('tasks.startTyping')"
            sourceDataDisplayField="displayName"
            :source-data="typeaheadDialog.sourceData"
            :itemTemplate="typeaheadDialog.itemTemplate"
            :valueToPreselect="typeaheadDialog.valueToPreselect"
            :context="typeaheadDialog.context">
        </typeahead-dialog>
    </div>    
</template>

<script>
import i18n from '@/i18n.bootstrap';
import sortBy from 'lodash-es/sortBy';
import moment from 'moment';
import { mapGetters } from 'vuex';
import Task from './Task';
import TaskDialog from './TaskDialog';
import TypeaheadDialog from '../../Shared/TypeaheadDialog';
import ProjectMembersDialogTpl from './ProjectMembersDialogTpl';

export default {
    name: 'task-group',

    props: ['taskListId', 'tasks'],

    components: { Task, TaskDialog, TypeaheadDialog },

    data() {
        return {
            filter: {
                options: [
                    { text: i18n.t('tasks.filter.showNotCompleted'), value: 'hide-completed'},
                    { text: i18n.t('tasks.filter.showAll'), value: 'show-all'}
                ],
                selected: 'show-all'
            },

            sorting: {
                column: 'subject',
                direction: 'asc'
            },

            typeaheadDialog: {
                sourceData: [],
                valueToPreselect: null,
                itemTemplate: ProjectMembersDialogTpl,
                context: {}
            }
        };
    },

    watch: {
        projectCollaborators() {
            this.typeaheadDialog.sourceData = this.projectCollaborators;
        }
    },

    computed: {
        ...mapGetters({
            projectCollaborators: 'projects/projectCollaborators'
        }),

        taskCount() {
            return this.tasksToDisplay.length || 0;
        },

        tasksToDisplay() {
            let result = [];
            
            // filtering
            if (this.filter.selected === 'show-all') {
                result = this.tasks;
            } else {
                result = this.tasks.filter(item => !item.complete);
            }

            // sorting
            let sortedResult = this.sortTasks(result, this.sorting.column);
            if (this.sorting.direction === 'desc') {
                sortedResult = sortedResult.reverse();
            }

            return sortedResult;
        },
        projectPermissions() {
          if(this.$store.state.projects.currentProject.projectData.role){
            return this.$store.state.projects.currentProject.projectData.role.permissions;
          } else {
            return [];
          }
        },
    },

    created() {
        
    },

    mounted() {
        this.$root.$on('project-tasks::select-collaborator', (payload) => {
            this.typeaheadDialog.valueToPreselect = this.projectCollaborators.find(item => item.id === payload.task.assignedTo) || null;

            this.typeaheadDialog.context.id = payload.task.id;
            this.typeaheadDialog.context.isAddCollaborator = payload.isAddCollaborator;
        });
    },

    methods: {
        // onFilterChanged(selectedOption) {
            
        // },

        createTask() {
            this.$root.$emit('bv::show::modal', 'dlgTask');
            this.$root.$emit('project-tasks::create-task');
        },

        handleSort(columnName) {
            if (columnName === this.sorting.column) {
                // reverse sort direction
                this.sorting.direction = (this.sorting.direction === 'asc') ? 'desc' : 'asc';
            } else {
                this.sorting.column = columnName;
                this.sorting.direction = 'asc';
            }
        },

        sortTasks(array, sortColumn) {
            if (sortColumn === 'complete') {
                return sortBy(array, [sortColumn]);
            }

            return array.sort((a, b) => {
                switch(sortColumn) {
                    case 'subject':
                    {
                        if (a[sortColumn].toLowerCase() < b[sortColumn].toLowerCase()) { return -1; }
                        if (a[sortColumn].toLowerCase() > b[sortColumn].toLowerCase()) { return 1; }
                        return 0;
                    }

                    case 'due':
                    case 'modified':
                    {
                        if (moment(a[sortColumn]) - moment(b[sortColumn]) < 0) { return -1; }
                        if (moment(a[sortColumn]) - moment(b[sortColumn]) > 0) { return 1; }
                        return 0;
                    }
                }
            });
        }
    }
}
</script>

<style>
    .custom-checkbox label::before{
        background-color: #ddd !important;
    }
</style>
